import React, { FC, memo, useEffect, useState } from 'react'

import { useFocusNodeById } from '@please/lrud'
import { scroller } from 'react-scroll'

import { orientation, Direction, focusIds } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { ScrollContainer } from '@/containers/styles'
import { useActiveBroadcast } from '@/hooks/useActiveBroadcast'

import { ChannelsList } from './ChannelsList'
import { BroadcastAsideElem } from './styles'

type Props = {
  isClassicHome?: boolean
  broadcastId: string
  focusIdPrefix?: string
}

const BroadcastAsideRaw: FC<Props> = ({
  isClassicHome,
  broadcastId,
  focusIdPrefix = '',
}) => {
  const [defaultFocusChild, setDefaultFocusChild] = useState<number>(0)
  const { activeChannel, channels } = useActiveBroadcast(broadcastId)
  const focusId = focusIdPrefix + broadcastId + 'BroadcastAsideElem'
  const focusNode = useFocusNodeById(focusId)

  useEffect(() => {
    if (
      focusNode &&
      focusNode.focusedChildIndex !== null &&
      focusNode?.focusedChildIndex !== defaultFocusChild
    ) {
      setDefaultFocusChild(focusNode.focusedChildIndex)
    }
  }, [focusNode?.focusedChildIndex])

  useEffect(() => {
    window.addEventListener('wheel', scrollHandle, {
      passive: false,
    } as AddEventListenerOptions)
    return () => {
      window.removeEventListener('wheel', scrollHandle, {
        passive: false,
      } as AddEventListenerOptions)
    }
  }, [])

  const scrollHandle = (event: WheelEvent) => {
    event.preventDefault()
    event.stopPropagation()
    return false
  }

  const prevChannelIndex =
    channels?.findIndex((el) => el.id === activeChannel?.id) || 0
  const normalizedPrevChannelIndex = prevChannelIndex > 0 ? prevChannelIndex : 0
  const channelsScrollOptions = isClassicHome
    ? scrollOptions.ChannelsListHomeClassicScroll
    : scrollOptions.ChannelsListGridScroll

  useEffect(() => {
    const prevChannelId = `${focusIds.CHANNEL_ITEM}${broadcastId}${activeChannel?.id}`
    setDefaultFocusChild(normalizedPrevChannelIndex)
    activeChannel && scroller.scrollTo(prevChannelId, channelsScrollOptions)
  }, [normalizedPrevChannelIndex, activeChannel?.id])

  const onMove = (e: MoveEvent) => {
    const nextFocusId = e.nextChildNode.focusId
    const currentChildIndex =
      e.direction === Direction.Forward
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setDefaultFocusChild(currentChildIndex)
    scroller.scrollTo(nextFocusId, channelsScrollOptions)
  }

  return (
    <BroadcastAsideElem
      focusId={focusId}
      orientation={orientation.VERTICAL}
      onMove={onMove}
      defaultFocusChild={defaultFocusChild}
      $isClassicHome={isClassicHome}
    >
      <ScrollContainer
        height={channelsScrollOptions.height}
        id={channelsScrollOptions.containerId}
      >
        <ChannelsList broadcastId={broadcastId} />
      </ScrollContainer>
    </BroadcastAsideElem>
  )
}

export const BroadcastAside = memo(BroadcastAsideRaw)
